import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import WeekSelector from '../common/WeekSelector';

const CreatePlanDropdown = ({ 
  onWeekChange, 
  weekRange, 
  onGeneratePlan, 
  onSavePlan 
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // Internal plan type state for the dropdown
  const [planType, setPlanType] = useState('weekly');
  
  // Meal preferences state
  const [mealPreferences, setMealPreferences] = useState({
    breakfast: '早餐',
    lunch: '1_肉类_1_蔬菜',
    dinner: '2_肉类_1_蔬菜_1_汤'
  });
  
  // Event form state
  const [eventData, setEventData] = useState({
    title: '',
    date: '',
    guestCount: 1,
    description: ''
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGenerateAndClose = () => {
    onGeneratePlan(mealPreferences);
    // Keep menu open so user can save after generating
  };
  
  const handleMealPrefChange = (meal, value) => {
    setMealPreferences(prev => ({
      ...prev,
      [meal]: value
    }));
  };

  const handleSaveAndClose = () => {
    if (planType === 'event') {
      onSavePlan('new', eventData);
    } else {
      onSavePlan('new');
    }
    handleClose();
  };

  const handlePlanTypeChange = (event, newPlanType) => {
    if (newPlanType !== null) {
      setPlanType(newPlanType);
    }
  };

  const handleEventFieldChange = (field, value) => {
    setEventData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isEventFormValid = () => {
    return eventData.title.trim() !== '' && 
           eventData.date !== '' && 
           eventData.guestCount > 0;
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        endIcon={<ArrowDownIcon />}
        onClick={handleClick}
        sx={{
          textTransform: "none",
          backgroundColor: "#1976d2",
          "&:hover": {
            backgroundColor: "#115293",
          },
        }}
      >
        {t('plans.createNewPlan')}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 400,
            maxWidth: 500,
            p: 3,
            borderRadius: 2
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Typography variant="h5" gutterBottom fontWeight={600}>
          {t('plans.createNewPlan')}
        </Typography>
        
        {/* Plan Type Toggle */}
        <Box display="flex" justifyContent="center" sx={{ my: 2 }}>
          <ToggleButtonGroup
            value={planType}
            exclusive
            onChange={handlePlanTypeChange}
            aria-label="plan type"
            size="medium"
            sx={{
              '& .MuiToggleButton-root': {
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                '&.Mui-selected': {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#115293',
                  }
                }
              }
            }}
          >
            <ToggleButton value="weekly" aria-label="weekly plans">
              {t('plans.weeklyPlans')}
            </ToggleButton>
            <ToggleButton value="event" aria-label="event plans">
              {t('plans.eventPlans')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {planType === 'weekly' ? (
          // Weekly Plan Form
          <>
            <Box sx={{ 
              mb: 3,
              p: 2, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 2 
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                {t('plans.selectDate', 'Select a Date')}
              </Typography>
              <WeekSelector
                onWeekChange={onWeekChange}
                weekRange={weekRange}
              />
            </Box>

            {/* Meal Preferences Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600} color="primary">
                {t('plans.mealPreferences', 'Meal Preferences')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                {t('plans.customizeYourMeals', 'Customize your meals for the week')}
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                    🌅 {t('plans.breakfast', 'Breakfast')}
                  </Typography>
                  <TextField
                    select
                    value={mealPreferences.breakfast}
                    onChange={(e) => handleMealPrefChange('breakfast', e.target.value)}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white'
                      }
                    }}
                  >
                    <MenuItem value="早餐">{t('plans.quick', 'Quick')}</MenuItem>
                  </TextField>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                    ☀️ {t('plans.lunch', 'Lunch')}
                  </Typography>
                  <TextField
                    select
                    value={mealPreferences.lunch}
                    onChange={(e) => handleMealPrefChange('lunch', e.target.value)}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white'
                      }
                    }}
                  >
                    <MenuItem value="1_肉类_1_蔬菜">{t('plans.1meat1veg', '1 Meat + 1 Veg')}</MenuItem>
                    <MenuItem value="2_蔬菜">{t('plans.2veg', '2 Veg')}</MenuItem>
                    <MenuItem value="1_肉类_2_蔬菜">{t('plans.1meat2veg', '1 Meat + 2 Veg')}</MenuItem>
                  </TextField>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                    🌙 {t('plans.dinner', 'Dinner')}
                  </Typography>
                  <TextField
                    select
                    value={mealPreferences.dinner}
                    onChange={(e) => handleMealPrefChange('dinner', e.target.value)}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white'
                      }
                    }}
                  >
                    <MenuItem value="1_肉类_1_蔬菜_1_汤">{t('plans.2meat1veg1soup', '2 Meat + 1 Veg + 1 Soup')}</MenuItem>
                    <MenuItem value="1_meat_1_veg_1_soup">{t('plans.1meat1veg1soup', '1 Meat + 1 Veg + 1 Soup')}</MenuItem>
                    <MenuItem value="2_meat_2_veg">{t('plans.2meat2veg', '2 Meat + 2 Veg')}</MenuItem>
                    <MenuItem value="1_meat_2_veg_1_soup">{t('plans.1meat2veg1soup', '1 Meat + 2 Veg + 1 Soup')}</MenuItem>
                  </TextField>
                </Box>
              </Stack>
            </Box>

            <Stack spacing={1.5}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleGenerateAndClose}
                disabled={!weekRange.startOfWeek}
                sx={{ 
                  textTransform: "none",
                  py: 1.2,
                  fontWeight: 600,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  }
                }}
              >
                ✨ {t('plans.generateWeeklyPlan')}
              </Button>
              
              <Button
                variant="contained"
                fullWidth
                onClick={handleSaveAndClose}
                disabled={!weekRange.startOfWeek}
                sx={{ 
                  textTransform: "none",
                  py: 1.2,
                  fontWeight: 600,
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#115293",
                  },
                }}
              >
                💾 {t('plans.saveNewPlan')}
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              {t('plans.selectWeekRange')}
            </Typography>
          </>
        ) : (
          // Event Plan Form
          <>
            <Box sx={{ 
              mb: 3,
              p: 2.5, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 2 
            }}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600} color="primary">
                {t('plans.eventDetails', 'Event Details')}
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField
                  label={t('plans.eventTitle')}
                  value={eventData.title}
                  onChange={(e) => handleEventFieldChange('title', e.target.value)}
                  fullWidth
                  required
                  size="small"
                  placeholder={t('plans.eventTitlePlaceholder', 'e.g., Birthday Party')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white'
                    }
                  }}
                />
                
                <TextField
                  label={t('plans.eventDate')}
                  type="date"
                  value={eventData.date}
                  onChange={(e) => handleEventFieldChange('date', e.target.value)}
                  fullWidth
                  required
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white'
                    }
                  }}
                />
                
                <TextField
                  label={t('plans.guestCount')}
                  type="number"
                  value={eventData.guestCount}
                  onChange={(e) => handleEventFieldChange('guestCount', parseInt(e.target.value) || 1)}
                  fullWidth
                  required
                  size="small"
                  inputProps={{ min: 1 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white'
                    }
                  }}
                />
                
                <TextField
                  label={t('plans.description')}
                  value={eventData.description}
                  onChange={(e) => handleEventFieldChange('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder={t('plans.eventDescriptionPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white'
                    }
                  }}
                />
              </Stack>
            </Box>

            <Button
              variant="contained"
              fullWidth
              onClick={handleSaveAndClose}
              disabled={!isEventFormValid()}
              sx={{ 
                textTransform: "none",
                py: 1.2,
                fontWeight: 600,
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#115293",
                },
              }}
            >
              🎉 {t('plans.createEvent')}
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              {t('plans.fillEventDetails')}
            </Typography>
          </>
        )}
      </Menu>
    </>
  );
};

export default CreatePlanDropdown;